/**
 * Wallet / daily-deal contract and pure helpers.
 *
 * The server owns every number here: balance, the reward amount, the entry
 * fee and all timestamps. The client only formats them and never decides how
 * many coins a player receives.
 */

// Literal paths so this module does not depend on the shared API constants.
export const COIN_WALLET_URL = '/coin-wallet';
export const COIN_REWARD_URL = '/coin-reward';

export type CoinTransactionType = 'earned' | 'spent';

export interface CoinWalletTransaction {
  readonly id: string;
  readonly amount: number;
  readonly type: CoinTransactionType;
  readonly description: string;
  readonly createdAt: string;
}

export interface CoinWallet {
  readonly balance: number;
  readonly earned: number;
  readonly spent: number;
  readonly lastFreeSpin: string | null;
  /** null when the daily deal can be claimed now. */
  readonly nextRewardAt: string | null;
  readonly rewardAvailable?: boolean;
  readonly rewardAmount: number;
  readonly rewardIntervalSeconds?: number;
  readonly entryFee: number;
  /** Server clock at response time; used to correct device clock skew. */
  readonly serverTime?: string;
  readonly transactions: CoinWalletTransaction[];
}

export interface CoinRewardResponse extends CoinWallet {
  readonly claimed: number;
}

export interface CoinRewardCooldown {
  readonly serverTime?: string;
  readonly message: string;
  readonly nextRewardAt: string;
  readonly code?: string;
}

/** Base cache key; always combined with the user id. */
export const COIN_WALLET_QUERY = 'coin-wallet';
export const coinWalletQueryKey = (userId: string) =>
  [COIN_WALLET_QUERY, userId].join('-');

/** Other existing caches that show a balance and must refresh after a claim. */
export const balanceQueryKeys = (userId: string): string[] => [
  coinWalletQueryKey(userId),
  ['currentUserCoins', userId].join('-'),
  'user-coins-info',
];

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** serverTime - deviceTime at the moment the response arrived. */
export const clockOffsetMs = (
  serverTime: string | null | undefined,
  receivedAt: number
): number => {
  const server = toMs(serverTime);
  return server === null ? 0 : server - receivedAt;
};

export type RewardStatus =
  | { readonly kind: 'ready' }
  | {
      readonly kind: 'cooldown';
      readonly nextRewardAt: number;
      readonly remainingMs: number;
    };

/**
 * Availability as of `now` (already corrected to server time). Mirrors the
 * server: nextRewardAt null means ready; a passed nextRewardAt is ready too.
 * Purely a display hint — the claim endpoint re-checks.
 */
export const rewardStatus = (
  wallet: Pick<CoinWallet, 'nextRewardAt'>,
  now: number
): RewardStatus => {
  const next = toMs(wallet.nextRewardAt);
  if (next === null || next <= now) return { kind: 'ready' };
  return { kind: 'cooldown', nextRewardAt: next, remainingMs: next - now };
};

const numberFormat = new Intl.NumberFormat('en-US');

export const formatCoins = (n: number | null | undefined): string =>
  numberFormat.format(
    Math.trunc(Number.isFinite(n as number) ? (n as number) : 0)
  );

export const formatSignedCoins = (amount: number, type: CoinTransactionType) =>
  `${type === 'spent' ? '−' : '+'}${formatCoins(Math.abs(amount))}`;

/** "11h 59m", "4m 05s", "12s". Never negative. */
export const formatCountdown = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

/** Accessible long form: "11 hours 59 minutes". */
export const describeCountdown = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  if (h) parts.push(`${h} hour${h === 1 ? '' : 's'}`);
  if (m) parts.push(`${m} minute${m === 1 ? '' : 's'}`);
  if (!parts.length) parts.push('less than a minute');
  return parts.join(' ');
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "at 9:40 PM", "tomorrow at 9:40 AM", "Nov 6 at 9:40 AM" (device locale/tz). */
export const formatUnlockTime = (
  target: number,
  now: number,
  locale?: string
): string => {
  const t = new Date(target);
  const time = t.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const today = new Date(now);
  if (sameDay(t, today)) return `today at ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sameDay(t, tomorrow)) return `tomorrow at ${time}`;
  const day = t.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return `${day} at ${time}`;
};

/** Relative label for the activity list: "Just now", "5m ago", "3h ago", "Nov 1". */
export const formatActivityTime = (
  iso: string,
  now: number,
  locale?: string
): string => {
  const at = toMs(iso);
  if (at === null) return '';
  const diff = Math.max(0, now - at);
  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 2 * 86_400_000) return 'Yesterday';
  const d = new Date(at);
  return d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== new Date(now).getFullYear()
      ? { year: 'numeric' }
      : {}),
  });
};

export type ActivityKind =
  | 'daily'
  | 'match-entry'
  | 'match-win'
  | 'chat'
  | 'invite'
  | 'other';

/**
 * Human labels for server ledger descriptions. Chat descriptions embed the
 * other person's id; it is deliberately not shown.
 */
export const describeTransaction = (
  tx: Pick<CoinWalletTransaction, 'description' | 'type'>
): { readonly label: string; readonly kind: ActivityKind } => {
  const d = (tx.description || '').trim();
  if (/^daily deal/i.test(d)) return { label: 'Daily deal', kind: 'daily' };
  if (/^match entry fee/i.test(d))
    return { label: 'Match entry', kind: 'match-entry' };
  if (/^reward for completing room/i.test(d))
    return { label: 'Match winnings', kind: 'match-win' };
  if (/^group chat entry fee/i.test(d))
    return { label: 'Group chat unlocked', kind: 'chat' };
  if (/^chat entry fee/i.test(d))
    return { label: 'Direct chat unlocked', kind: 'chat' };
  if (/invite/i.test(d)) return { label: 'Invite reward', kind: 'invite' };
  if (d)
    return { label: d.length > 40 ? `${d.slice(0, 39)}…` : d, kind: 'other' };
  return {
    label: tx.type === 'spent' ? 'Coins spent' : 'Coins earned',
    kind: 'other',
  };
};

/** Extracts a 409 cooldown body from an axios error response (useHttpMutation rethrows `err.response`). */
export const readCooldown = (err: unknown): CoinRewardCooldown | null => {
  const res = err as { status?: number; data?: Partial<CoinRewardCooldown> };
  if (res?.status === 409 && res.data?.nextRewardAt) {
    return {
      message: res.data.message || 'Daily deal already claimed.',
      nextRewardAt: res.data.nextRewardAt,
      code: res.data.code,
      ...(res.data.serverTime ? { serverTime: res.data.serverTime } : {}),
    };
  }
  return null;
};
